import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHead } from './Table';
import { Card, CardContent } from './Card';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import { ChevronRight, MoreVertical } from 'lucide-react';

interface Column {
  key: string;
  title: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, item: any) => React.ReactNode;
  className?: string;
  mobileHidden?: boolean;
}

interface DataNavigatorProps {
  columns: Column[];
  data: any[];
  isLoading?: boolean;
  onRowClick?: (item: any) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  mobileCardRender?: (item: any) => React.ReactNode;
  className?: string;
}

export const DataNavigator = ({
  columns,
  data,
  isLoading,
  onRowClick,
  emptyTitle = 'Không có dữ liệu',
  emptyDescription = 'Hiện chưa có dữ liệu để hiển thị.',
  mobileCardRender,
  className = '',
}: DataNavigatorProps) => {

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Desktop Skeleton */}
        <div className="hidden lg:block">
          <Table isLoading={true} columns={columns} />
        </div>
        {/* Mobile Skeleton */}
        <div className="lg:hidden space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-[24px]" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Desktop View: Advanced Table */}
      <div className="hidden lg:block overflow-hidden rounded-[32px] border border-white/20 bg-white/40 backdrop-blur-xl shadow-sm hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-500">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/30 backdrop-blur-md border-b border-slate-100/50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-6 py-4 text-xs font-medium tracking-widest text-slate-400 ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.className || ''}`}
                >
                  {col.title}
                </th>
              ))}
              {onRowClick && <th className="w-10"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            <AnimatePresence mode="popLayout">
              {data.map((item, idx) => (
                <motion.tr
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  key={item.id || idx}
                  onClick={() => onRowClick?.(item)}
                  className={`group hover:bg-slate-50/50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-6 py-4 text-sm font-medium text-slate-700 ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.className || ''}`}
                    >
                      {col.render ? col.render(item[col.key], item) : (item[col.key] ?? '-')}
                    </td>
                  ))}
                  {onRowClick && (
                    <td className="px-4 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-200 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                    </td>
                  )}
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Mobile View: Rich Card List */}
      <div className="lg:hidden space-y-4">
        <AnimatePresence mode="popLayout">
          {data.map((item, idx) => (
            <motion.div
              layout
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              key={item.id || idx}
              onClick={() => onRowClick?.(item)}
            >
              <Card variant="glass" className="active:scale-[0.98] transition-all rounded-[32px] overflow-hidden">
                <CardContent className="p-5">
                  {mobileCardRender ? (
                    mobileCardRender(item)
                  ) : (
                    <div className="flex justify-between items-start">
                      <div className="space-y-3 flex-1">
                        {columns.slice(0, 3).map((col, cIdx) => (
                          <div key={col.key} className="flex flex-col">
                            {cIdx === 0 ? (
                              <span className="text-sm font-black text-slate-900 line-clamp-1">{item[col.key]}</span>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-400 w-16">{col.title}</span>
                                <span className="text-xs font-medium text-slate-600">{col.render ? col.render(item[col.key], item) : (item[col.key] ?? '-')}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="p-2 rounded-xl bg-slate-50 text-slate-400">
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
